import { DEFAULT_DESCRIPTION, DEFAULT_KEYWORDS, DEFAULT_OG_IMAGE, DEFAULT_TITLE, SITE_NAME } from './siteConfig.js';

function inferImageType(image = '') {
  const pathname = String(image || '').split('?')[0].toLowerCase();
  if (pathname.endsWith('.png')) return 'image/png';
  if (pathname.endsWith('.webp')) return 'image/webp';
  return 'image/jpeg';
}

function upsertHeadAttribute(selector, tagName, seedAttributes, attributeName, value) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement(tagName);
    Object.entries(seedAttributes).forEach(([key, seedValue]) => {
      element.setAttribute(key, seedValue);
    });
    document.head.appendChild(element);
  }

  element.setAttribute(attributeName, value);
}

function setManagedStructuredData(structuredData = []) {
  document.head
    .querySelectorAll('script[type="application/ld+json"][data-seo-managed="true"]')
    .forEach((element) => element.remove());

  const items = Array.isArray(structuredData)
    ? structuredData.filter(Boolean)
    : structuredData
      ? [structuredData]
      : [];

  items.forEach((schema, index) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.seoManaged = 'true';
    script.dataset.seoIndex = String(index);
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}

export function applyPageMeta(meta) {
  if (typeof document === 'undefined') return;

  const title = meta?.title || DEFAULT_TITLE;
  const description = meta?.description || DEFAULT_DESCRIPTION;
  const keywords = meta?.keywords || DEFAULT_KEYWORDS;
  const canonical = meta?.canonical || window.location.href;
  const image = meta?.image || DEFAULT_OG_IMAGE;
  const imageType = meta?.imageType || inferImageType(image);
  const imageAlt = meta?.imageAlt || title;
  const imageWidth = String(meta?.imageWidth || 1200);
  const imageHeight = String(meta?.imageHeight || 630);
  const type = meta?.type || 'website';
  const robots = meta?.robots || 'index, follow';

  document.title = title;

  upsertHeadAttribute('meta[name="title"]', 'meta', { name: 'title' }, 'content', title);
  upsertHeadAttribute('meta[name="description"]', 'meta', { name: 'description' }, 'content', description);
  upsertHeadAttribute('meta[name="keywords"]', 'meta', { name: 'keywords' }, 'content', keywords);
  upsertHeadAttribute('meta[name="robots"]', 'meta', { name: 'robots' }, 'content', robots);
  upsertHeadAttribute('link[rel="canonical"]', 'link', { rel: 'canonical' }, 'href', canonical);

  upsertHeadAttribute('meta[property="og:type"]', 'meta', { property: 'og:type' }, 'content', type);
  upsertHeadAttribute('meta[property="og:url"]', 'meta', { property: 'og:url' }, 'content', canonical);
  upsertHeadAttribute('meta[property="og:title"]', 'meta', { property: 'og:title' }, 'content', title);
  upsertHeadAttribute('meta[property="og:description"]', 'meta', { property: 'og:description' }, 'content', description);
  upsertHeadAttribute('meta[property="og:image"]', 'meta', { property: 'og:image' }, 'content', image);
  upsertHeadAttribute('meta[property="og:image:secure_url"]', 'meta', { property: 'og:image:secure_url' }, 'content', image);
  upsertHeadAttribute('meta[property="og:image:type"]', 'meta', { property: 'og:image:type' }, 'content', imageType);
  upsertHeadAttribute('meta[property="og:image:width"]', 'meta', { property: 'og:image:width' }, 'content', imageWidth);
  upsertHeadAttribute('meta[property="og:image:height"]', 'meta', { property: 'og:image:height' }, 'content', imageHeight);
  upsertHeadAttribute('meta[property="og:image:alt"]', 'meta', { property: 'og:image:alt' }, 'content', imageAlt);
  upsertHeadAttribute('meta[property="og:site_name"]', 'meta', { property: 'og:site_name' }, 'content', SITE_NAME);
  upsertHeadAttribute('meta[property="og:locale"]', 'meta', { property: 'og:locale' }, 'content', 'en_PH');

  upsertHeadAttribute('meta[name="twitter:card"]', 'meta', { name: 'twitter:card' }, 'content', 'summary_large_image');
  upsertHeadAttribute('meta[name="twitter:url"]', 'meta', { name: 'twitter:url' }, 'content', canonical);
  upsertHeadAttribute('meta[name="twitter:title"]', 'meta', { name: 'twitter:title' }, 'content', title);
  upsertHeadAttribute('meta[name="twitter:description"]', 'meta', { name: 'twitter:description' }, 'content', description);
  upsertHeadAttribute('meta[name="twitter:image"]', 'meta', { name: 'twitter:image' }, 'content', image);
  upsertHeadAttribute('meta[name="twitter:image:alt"]', 'meta', { name: 'twitter:image:alt' }, 'content', imageAlt);

  setManagedStructuredData(meta?.structuredData || []);
}
